// src/pages/Patisserie/PatisserieListPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Container,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import api from "../../services/api";
import PatisserieItemCard from "./PatisserieItemCard";
import useDiscountedProducts from "../../hooks/useDiscountedProducts";
import SortAndFilterControls from "./SortAndFilterControls";

const categoryLabelMap = {
  cheesecakeSlice: "Cheesecake Slices",
  cakeSlice: "Cake Slices",
  pastry: "Pastries",
  cupcake: "Cupcakes",
  cookie: "Cookies",
  macaron: "Macarons",
};

const formatCategoryName = (str) =>
  str.replace(/([A-Z])/g, " $1").replace(/^\w/, (c) => c.toUpperCase());

const allDietaryOptions = ["gluten free", "vegan", "dairy free"];

const listUnitOf = (p) => Number(p?.orderConfig?.regular?.price ?? 0);
const itemsPerSetOf = (p) => Number(p?.orderConfig?.regular?.itemsPerSet ?? 1);

const PatisserieListPage = () => {
  const location = useLocation();

  const [rawItems, setRawItems] = useState([]);
  const discountedItems = useDiscountedProducts(rawItems);

  const [sortOption, setSortOption] = useState("alphabetical_asc");
  const [filterTags, setFilterTags] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(true);

  const query = new URLSearchParams(location.search);
  const categories = useMemo(
    () => query.get("categories")?.split(",") || [],
    [location.search]
  );

  const formattedHeading = useMemo(() => {
    return categories.length === 1
      ? categoryLabelMap[categories[0]] || formatCategoryName(categories[0])
      : "Patisserie";
  }, [categories]);

  useEffect(() => {
    document.title = "Patisserie";
  }, []);

  useEffect(() => {
    const fetchPatisserieItems = async () => {
      setLoading(true);
      try {
        const res = await api.get("/patisserie", { params: { categories } });

        const filtered = (res.data || []).filter(
          (item) =>
            item.isActive !== false && item.availability?.regularOrder === true
        );

        setRawItems(filtered);

        // initialize quantity by each item's itemsPerSet
        const initial = {};
        for (const item of filtered) {
          initial[item._id] = itemsPerSetOf(item);
        }
        setQuantities(initial);

        // Analytics
        if (window.fbq && filtered.length > 0) {
          filtered.forEach((item) => {
            window.fbq("track", "ViewContent", {
              content_name: item.displayName || item.name,
              content_ids: [item._id],
              content_type: "product",
              value: listUnitOf(item),
              currency: "CAD",
            });
          });
        }

        if (window.gtag && filtered.length > 0) {
          window.gtag("event", "view_item_list", {
            item_list_name: formattedHeading,
            items: filtered.map((item, index) => ({
              item_id: item._id,
              item_name: item.displayName || item.name,
              item_category: item.category || "patisserie",
              item_list_name: formattedHeading,
              index,
              price: listUnitOf(item),
            })),
          });
        }
      } catch (err) {
        console.error("Error fetching patisserie items:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPatisserieItems();
  }, [categories, formattedHeading]);

  // Filter (tags are strings; item.dietaryTags may be [{label}] or strings—handle both)
  const filteredItems = useMemo(() => {
    if (!filterTags.length) return discountedItems;
    return discountedItems.filter((item) => {
      const tags = item.dietaryTags || [];
      return tags.some((t) =>
        filterTags.includes(typeof t === "string" ? t : t.label)
      );
    });
  }, [discountedItems, filterTags]);

  // Sort by UI price (discounted display) or name
  const sortedItems = useMemo(() => {
    const arr = [...filteredItems];
    return arr.sort((a, b) => {
      const nameA = (a.displayName || a.name || "").toLowerCase();
      const nameB = (b.displayName || b.name || "").toLowerCase();
      const priceA = Number(a?.display?.unit ?? listUnitOf(a));
      const priceB = Number(b?.display?.unit ?? listUnitOf(b));

      switch (sortOption) {
        case "alphabetical_desc":
          return nameB.localeCompare(nameA);
        case "price_asc":
          return priceA - priceB;
        case "price_desc":
          return priceB - priceA;
        default:
          return nameA.localeCompare(nameB);
      }
    });
  }, [filteredItems, sortOption]);

  return (
    <Container maxWidth="xl" disableGutters>
      <Box sx={{ py: 4, px: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mb={2}>
          <Typography variant="h4" fontWeight="bold">
            {formattedHeading}
          </Typography>

          <SortAndFilterControls
            setSortOption={setSortOption}
            filterTags={filterTags}
            setFilterTags={setFilterTags}
            allDietaryOptions={allDietaryOptions}
          />
        </Stack>

        {filterTags.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            mb={2}>
            {filterTags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onDelete={() =>
                  setFilterTags((prev) => prev.filter((t) => t !== tag))
                }
                color="primary"
              />
            ))}
            <Button onClick={() => setFilterTags([])} size="small">
              Clear All
            </Button>
          </Stack>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress />
          </Box>
        ) : sortedItems.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              mt: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}>
            <img
              src="/images/no_results_found.png"
              alt="No Results"
              style={{ width: "400px", maxWidth: "90%", marginBottom: 24 }}
            />
            <Typography variant="h6" color="text.secondary">
              Please check back later or browse other categories.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {sortedItems.map((item) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                <PatisserieItemCard
                  item={item}
                  quantity={quantities[item._id]}
                  setQuantity={(newVal) =>
                    setQuantities((prev) => ({ ...prev, [item._id]: newVal }))
                  }
                  isAvailable={item.isAvailable !== false}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default PatisserieListPage;
